/* Data returned from API */
export type LicenseData = {
  /* Vehicle license plate */
  license: string;
  /* Two letter country code, e.g. FI, SE */
  country: string;
  /** Vehicle data associated with license from fitment */
  vehicleData: {
    chassisData: {
      centerBore: number;
      chassisId: string;
      frontAxleWeight: number;
      maxSpeed: number;
      maximumWheelLoad: number;
      offset: string;
      pitchCirleDiameter: string;
      rearAxleWeight: number;
      tpms: string;
    },
    modelData: {
      constructedFrom: number;
      constructedTo: number | null,
      fullName: string;
      fullNameSlug: string;
      kType: string;
      manufacturerId: string;
      manufacturerName: string;
      manufacturerNameSlug: string;
      modelId: string;
      modelName: string;
      modelNameSlug: string;
      modelVariantName: string;
      modelVariantNameSlug: string;
      powerHP: number;
      powerKW: number;
    },
    registrationData: {
      licensePlateNumber: string;
      yearOfFirstRegistration: number;
    },
    rimFitmentData: Array<{
      tyreSizes: string[];
      rimSize: string;
      minOffset: number;
      maxOffset: number;
      axle: string;
      isOemSize: boolean;
      isFactoryDefaultSize: boolean;
      offset: number | null;
    }>;
    tyreFitmentData: Array<{
      tyreSize: string;
      speedIndex: string;
      loadIndex: number;
      axle: string;
      isOemSize: boolean;
      isFactoryDefaultSize: boolean;
      sizeString: string;
      ms: boolean;
    }>;
  }
};

export type Options = Array<{id: string; name: string;}>;

export type TireSize = {
  /* Short size string, e.g. 215/60R16 */
  size: string;
  /* Tire width in mm */
  width: number;
  /* Tire height is actually aspect ratio (height to width ratio) */
  height: number;
  /* Rim diameter in inches */
  rim: number;
  /* Vehicle manufacturer's default size */
  default?: boolean;
};

type FitmentApiOptions = {
  lang?: string;
}

export class FitmentApi {
  #lang = document.documentElement.getAttribute('stage-lang');
  #apiUrl = '/stage-ajax/nt-fitment-api';
  #abortController: AbortController | null = null;

  constructor(options: FitmentApiOptions = {}) {
    if(options.lang) {
      this.#lang = options.lang;
    }
  }

  async #request(endpoint, queryParams?: Record<string, string>) {
    if(this.#abortController) {
      this.#abortController.abort('REQUEST_CANCELED');
    }

    this.#abortController = new AbortController();

    const queryString = new URLSearchParams({
      ...queryParams,
      'stage-language': this.#lang || 'en',
    }).toString();

    try {
      const response = await fetch(`${this.#apiUrl}/${endpoint}?${queryString}`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'xhr',
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        signal: this.#abortController.signal,
      });

      this.#abortController = null;

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[FitmentApi] #request failed:', response.status, response.statusText, errorText);
        throw new Error(errorText);
      }

      return await response.json();
    } catch(error) {
      this.#abortController = null;
      console.error('[FitmentApi] #request error:', error);
      throw error;
    }

  }

  /**
   * Stores api data to session storage, to reduce number of api calls.
   */
  #storeData(key: string, data: any) {
    if(typeof key !== 'string' || !key.length) {
      return;
    }

    if(typeof data !== 'object') {
      return;
    }

    sessionStorage.setItem(key, JSON.stringify(data));
  }

  /**
   * Returns data from session storage or false if not found.
   */
  #getStoredData(key: string): boolean | any {
    if(typeof key !== 'string' || !key.length) {
      return false;
    }

    const storedData = sessionStorage.getItem(key);
    if(storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if(parsedData) {
          return parsedData;
        }
      } catch(e) {
        console.error('[FitmentApi]: Failed to parse data from storage', e);
      }
    }

    return false;
  }

  // NA endpoints

  async getNAManufacturers(year: string): Promise<Options> {
    this.#log('getNAManufacturers()', {year});

    if(!year) {
      return Promise.reject('Year not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitmentna_mode:manufacturers_year:${year}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored manufacturers', storedData);
      return Promise.resolve(storedData as Options);
    } else {
      this.#log('no stored manufacturers, fetching from API');
      const manufacturerData = await this.#request(`na/years/${year}/manufacturers`) as Options;
      this.#storeData(storageKey, manufacturerData);
      return manufacturerData;
    }
  }

  async getNAModels(params: {year: string; manufacturerId: string;}): Promise<Options> {
    this.#log('getNAModels()', params);

    if(!params.year || !params.manufacturerId) {
      return Promise.reject('Year or manufacturer ID not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitmentna_mode:models_manufacturer:${params.manufacturerId}_year:${params.year}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored models', storedData);
      return Promise.resolve(storedData as Options);
    } else {
      this.#log('no stored models, fetching from API');
      const modelData = await this.#request(`na/years/${params.year}/manufacturers/${params.manufacturerId}/models`) as Options;
      this.#storeData(storageKey, modelData);
      return modelData;
    }
  }

  async getNASubmodels(params: {year: string; manufacturerId: string; modelId: string;}): Promise<Options> {
    this.#log('getNASubmodels()', params);

    if(!params.year || !params.manufacturerId || !params.modelId) {
      return Promise.reject('Year, manufacturer ID or model ID not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitmentna_mode:submodels_manufacturer:${params.manufacturerId}_year:${params.year}_model:${params.modelId}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored submodels', storedData);
      return Promise.resolve(storedData as Options);
    } else {
      this.#log('no stored submodels, fetching from API');
      const submodelData = await this.#request(`na/years/${params.year}/manufacturers/${params.manufacturerId}/models/${params.modelId}/submodels`) as Options;
      this.#storeData(storageKey, submodelData);
      return submodelData;
    }
  }

  async getNATireSizes(params: {modelId: string; chassisId: string;}): Promise<TireSize[]> {
    this.#log('getNATireSizes()', params);

    if(!params.modelId || !params.chassisId) {
      return Promise.reject('Model ID or chassis ID not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitmentna_mode:sizes_model:${params.modelId}_chassis:${params.chassisId}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored sizes', storedData);
      return Promise.resolve(storedData as TireSize[]);
    } else {
      this.#log('no stored sizes, fetching from API');
      const sizesData = await this.#request(`na/tire-sizes/${params.modelId}/${params.chassisId}`) as TireSize[];
      this.#storeData(storageKey, sizesData);
      return sizesData;
    }
  }

  // Rest of the world enpoints

  async getVehicleDataFromLicense(license: string, country: string): Promise<LicenseData> {
    this.#log('getVehicleDataFromLicense()', {license, country});

    if(!license || !country) {
      return Promise.reject('License or country not provided');
    }

    const storageKey = `c:${country}_v:fitment_mode:license_license:${license.replace(/\W/g, '_')}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored license', storedData);
      return Promise.resolve(storedData as LicenseData);
    } else {
      this.#log('no stored license, fetching from API');
      const licenseData = await this.#request(`license/${encodeURIComponent(license)}/${encodeURIComponent(country)}`) as LicenseData;
      this.#storeData(storageKey, licenseData);
      return licenseData;
    }
  }

  async getYears(manufacturerId: string): Promise<Options> {
    this.#log('getYears()', {manufacturerId});

    if(!manufacturerId) {
      return Promise.reject('Manufacturer ID not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitment_mode:years_manufacturer:${manufacturerId}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored years data', storedData);
      return Promise.resolve(storedData as Options);
    } else {
      this.#log('no stored years data found, fetching from API');
      const yearsData = await this.#request(`manufacturers/${encodeURIComponent(manufacturerId)}/years`) as Options;
      this.#storeData(storageKey, yearsData);
      return yearsData;
    }
  }

  async getModels(params: {manufacturerId: string; year: string;}): Promise<Options> {
    this.#log('getManufacturerModels()', params);

    if(!params.manufacturerId || !params.year) {
      return Promise.reject('Manufacturer ID or year not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitment_mode:models_manufacturer:${params.manufacturerId}_year:${params.year}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored models', storedData);
      return Promise.resolve(storedData as Options);
    } else {
      this.#log('no stored models, fetching from API');
      const modelsData = await this.#request(`manufacturers/${encodeURIComponent(params.manufacturerId)}/years/${encodeURIComponent(params.year)}/models`) as Options;
      this.#storeData(storageKey, modelsData);
      return modelsData;
    }
  }

  async getVehicles(params: {manufacturerId: string; year: string; modelId: string;}): Promise<Options> {
    this.#log('getManufacturerVehicles()', params);

    if(!params.manufacturerId || !params.year || !params.modelId) {
      return Promise.reject('Manufacturer ID, year or model ID not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitment_mode:vehicles_manufacturer:${params.manufacturerId}_year:${params.year}_model:${params.modelId}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored vehicles', storedData);
      return Promise.resolve(storedData as Options);
    } else {
      this.#log('no stored vehicles, fetching from API');
      const vehiclesData = await this.#request(`manufacturers/${encodeURIComponent(params.manufacturerId)}/years/${encodeURIComponent(params.year)}/models/${encodeURIComponent(params.modelId)}/vehicles`) as Options;
      this.#storeData(storageKey, vehiclesData);
      return vehiclesData;
    }
  }

  async getTireSizesByVehicle(vehicleId: string): Promise<TireSize[]> {
    this.#log('getTireSizesByVehicle()', vehicleId);

    if(!vehicleId) {
      return Promise.reject('Vehicle ID not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitment_mode:sizes_vehicle:${vehicleId}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored sizes', storedData);
      return Promise.resolve(storedData as TireSize[]);
    } else {
      this.#log('no stored sizes, fetching from API');
      const sizesData = await this.#request(`tire-sizes/by-vehicle/${encodeURIComponent(vehicleId)}`) as TireSize[];
      this.#storeData(storageKey, sizesData);
      return sizesData;
    }
  }

  async getTireSizesByModel(params: {year: string; modelId: string;}): Promise<TireSize[]> {
    this.#log('getTireSizesByModel()', params);

    if(!params.year || !params.modelId) {
      return Promise.reject('Year or model ID not provided');
    }

    const storageKey = `l:${this.#lang}_v:fitment_mode:sizes_model:${params.modelId}_year:${params.year}`;
    const storedData = this.#getStoredData(storageKey);

    if(storedData) {
      this.#log('returning stored sizes', storedData);
      return Promise.resolve(storedData as TireSize[]);
    } else {
      this.#log('no stored sizes, fetching from API');
      const sizesData = await this.#request(`tire-sizes/by-model/${encodeURIComponent(params.modelId)}/${encodeURIComponent(params.year)}`) as TireSize[];
      this.#storeData(storageKey, sizesData);
      return sizesData;
    }
  }

  #log(message: string, ...optionalParams: any[]) {
    // Enable for debugging
    // console.log(`[FitmentApi] ${message}`, ...optionalParams);
  }
}

export default new FitmentApi();
